import { useState, useEffect, useRef } from 'react'
import { User } from '@supabase/supabase-js'
import { supabase } from '../config/supabase'

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  
  // Track confirmed user to prevent flickering during token refresh
  const confirmedUser = useRef<User | null>(null)

  useEffect(() => {
    let isMounted = true

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return
      
      const currentUser = session?.user ?? null
      confirmedUser.current = currentUser
      setUser(currentUser)
      checkIsAdmin(currentUser)
      setLoading(false)
    })

    // Listen for auth changes - but only react to meaningful events
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return
      
      console.log('useAuth: Auth event:', event, 'Has session:', !!session)
      
      // Only sign out on explicit SIGNED_OUT event
      if (event === 'SIGNED_OUT') {
        confirmedUser.current = null
        setUser(null)
        setIsAdmin(false)
        setLoading(false)
        return
      }
      
      // If we get a valid session, update user
      if (session?.user) {
        confirmedUser.current = session.user
        setUser(session.user)
        checkIsAdmin(session.user)
      }
      // IGNORE events with null session (except SIGNED_OUT)
      // This prevents flickering during token refresh
      
      setLoading(false)
    })

    return () => {
      isMounted = false
      subscription.unsubscribe()
    }
  }, [])

  const checkIsAdmin = (currentUser: User | null) => {
    if (!currentUser) {
      setIsAdmin(false)
      return
    }

    // Check if user has admin role in app_metadata
    const role = currentUser.app_metadata?.role
    setIsAdmin(role === 'admin')
  }

  return { user, loading, isAdmin }
}
